/**
 * @copyright 2024-2026 David Baugh
 * @license Proprietary - All Rights Reserved
 * Unauthorized copying, modification, or distribution is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Progress } from "./ui/progress";
import { Calendar, Clock, AlertCircle, CheckCircle2, XCircle, HelpCircle, Book, BookOpen, ChevronDown, ChevronUp, User, Target, GraduationCap, UserPlus } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';
import EnrollmentModal from './student/EnrollmentModal';

// Place these utility functions outside any component
const formatTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) {
    return `${hours}h ${remainingMinutes}m`;
  }
  return `${remainingMinutes}m`;
};

const formatTimeRemaining = (minutes) => {
  if (minutes <= 0) return 'Available now';
  
  const totalSeconds = Math.ceil(minutes * 60);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  
  if (mins === 0) {
    return `${secs} seconds`;
  } else if (mins === 1) {
    return `1 minute ${secs > 0 ? `${secs} ${secs === 1 ? 'second' : 'seconds'}` : ''}`;
  } else {
    return `${mins} minutes ${secs > 0 ? `${secs} ${secs === 1 ? 'second' : 'seconds'}` : ''}`;
  }
};

const calculateProgress = (percentage) => {
  // Debug logging to understand what we're receiving
  console.log('calculateProgress input:', percentage, 'type:', typeof percentage);
  
  if (percentage === null || percentage === undefined) return 0;
  
  // Convert string percentages to numbers
  const numericPercentage = typeof percentage === 'string' ? parseFloat(percentage) : percentage;
  
  if (typeof numericPercentage !== 'number' || isNaN(numericPercentage)) return 0;
  if (numericPercentage < 0) return 0;
  if (numericPercentage > 100) return 100;
  return numericPercentage;
};

const TestCard = ({ test, onStartTest, onReviewTest }) => {
  // Hook declarations MUST be inside the component function
  const [timeLeft, setTimeLeft] = useState(test.timeoutRemaining || 0);
  const [countdownActive, setCountdownActive] = useState(!test.isAvailable && !test.passed && !test.attemptsExhausted);
  const [isNowAvailable, setIsNowAvailable] = useState(test.isAvailable);
  const [showNotification, setShowNotification] = useState(false);
  
  // Calculate countdown progress percentage using dynamic cooldown period
  const totalWaitTime = test.retryCooldownMinutes ?? 5; // Use test's cooldown setting or default to 5 minutes
  const progressPercentage = totalWaitTime > 0 ? Math.min(100, Math.max(0, (totalWaitTime - timeLeft) / totalWaitTime * 100)) : 0;
  
  // useEffect MUST be inside the component function
  useEffect(() => {
    if (!countdownActive || timeLeft <= 0) return;
    
    const intervalId = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = Math.max(0, prev - (1/60)); // Decrease by 1 second (1/60 of a minute)
        
        if (newTime <= 0) {
          clearInterval(intervalId);
          setCountdownActive(false);
          setIsNowAvailable(true);
          
          // Show notification that test is now available
          if (!test.passed && !test.attemptsExhausted) {
            setShowNotification(true);
            // Auto-hide notification after 5 seconds
            setTimeout(() => setShowNotification(false), 5000);
          }
          
          return 0;
        }
        
        return newTime;
      });
    }, 1000);
    
    return () => clearInterval(intervalId);
  }, [countdownActive, timeLeft, test.passed, test.attemptsExhausted]);

  return (
    <div className="mb-4 sm:mb-6 p-4 sm:p-6 border rounded-lg shadow-sm hover:shadow-md transition-shadow bg-white">
      {/* Notification Banner */}
      {showNotification && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 rounded-lg flex items-center animate-pulse">
          <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
          <span className="text-green-800 font-medium">
            Test "{test.name}" is now available! You can start it whenever you're ready.
          </span>
          <button 
            onClick={() => setShowNotification(false)}
            className="ml-auto text-green-600 hover:text-green-800"
          >
            ×
          </button>
        </div>
      )}
      
      {/* Test Header Section */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4 gap-3">
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-semibold mb-2 flex items-center break-words">
            {test.name}
            {test.passed && <CheckCircle2 className="h-5 w-5 text-green-500 ml-2 flex-shrink-0" />}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm text-gray-600">
            {/* Test Details */}
            <div className="flex items-center">
              <Clock className="h-4 w-4 mr-2" />
              <span>{formatTime(test.timeLimit)}</span>
            </div>
            <div className="flex items-center">
              <Book className="h-4 w-4 mr-2" />
              <span>{test.maxRetries === 0 ? '∞' : test.maxRetries} attempts</span>
            </div>
            <div className="flex items-center">
              <HelpCircle className="h-4 w-4 mr-2" />
              <span>{test.total_questions || '?'} questions</span>
            </div>
            <div className="flex items-center">
              <Target className="h-4 w-4 mr-2" />
              <span>{test.pass_threshold || 90}% to pass</span>
            </div>
            {test.assignedBy && (
              <div className="flex items-center">
                <User className="h-4 w-4 mr-2" />
                <span>Assigned by: {test.assignedBy}</span>
              </div>
            )}
            {test.dueDate && (
              <div className="flex items-center text-amber-600">
                <Calendar className="h-4 w-4 mr-2" />
                <span>Due: {new Date(test.dueDate).toLocaleDateString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Score Display */}
        {test.percentage != null && (
          <div className="text-left sm:text-right sm:min-w-[150px] flex-shrink-0">
            <div className="mb-2">
              <div className="text-sm text-gray-600 mb-1">Last Attempt:</div>
              <div className="flex items-center justify-start sm:justify-end">
                <Progress 
                  value={calculateProgress(test.percentage)} 
                  className="w-24 mr-2"
                />
                <span className="font-medium">
                  {typeof test.percentage === 'number' ? test.percentage.toFixed(1) : test.percentage}%
                </span>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Attempts: {test.attempts.current}/{test.attempts.max === 'Unlimited' ? '∞' : test.attempts.max}
            </div>
          </div>
        )}
      </div>

      {/* Status and Actions Section */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-4 border-t gap-3">
        <div>
          {test.passed ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <CheckCircle2 className="h-4 w-4 mr-1" />
              Passed
            </span>
          ) : test.attemptsExhausted ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
              <XCircle className="h-4 w-4 mr-1" />
              Maximum Attempts Reached
            </span>
          ) : test.percentage != null ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
              <AlertCircle className="h-4 w-4 mr-1" />
              Not Passed
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
              <HelpCircle className="h-4 w-4 mr-1" />
              Not Attempted
            </span>
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          {test.percentage != null && (
            <Button
              onClick={() => onReviewTest(test.id)}
              variant="outline"
              className="flex items-center justify-center min-h-[44px] w-full sm:w-auto"
            >
              Review Last Attempt
            </Button>
          )}

          {!test.attemptsExhausted && (
            isNowAvailable ? (
              <Button
                onClick={() => onStartTest(test.id)}
                className="flex items-center justify-center min-h-[44px] w-full sm:w-auto"
              >
                {test.passed ? 'Improve Score' : 'Start Test'}
              </Button>
            ) : (
              <div className="text-sm text-gray-600 flex flex-col items-start sm:items-end">
                {test.retryCooldownMinutes === -1 ? (
                  <div className="flex items-center mb-1">
                    <XCircle className="h-4 w-4 mr-1 text-red-500" />
                    <span className="text-red-600 font-medium">
                      Retries disabled after failure
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center mb-1">
                      <Clock className="h-4 w-4 mr-1 text-amber-500" />
                      <span className="text-amber-600 font-medium">
                        Available in {formatTimeRemaining(timeLeft)}
                      </span>
                    </div>
                    {totalWaitTime > 0 && (
                      <div className="w-full bg-gray-200 rounded-full h-2 relative overflow-hidden">
                        <div
                          className="bg-amber-500 h-2 rounded-full transition-all duration-1000 ease-linear"
                          style={{ width: `${progressPercentage}%` }}
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            )
          )}
        </div>
      </div>

      {/* Additional Info Section */}
      {test.attemptsExhausted && !test.passed && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
          <AlertCircle className="inline-block h-4 w-4 mr-2" />
          Maximum attempts reached. Please contact your instructor for assistance.
        </div>
      )}

      {test.passed && !test.attemptsExhausted && (
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm">
          <CheckCircle2 className="inline-block h-4 w-4 mr-2" />
          You've passed this test! You can still retake it to improve your score if you'd like.
        </div>
      )}
    </div>
  );
};


const CohortSection = ({ cohort, onStartTest, onReviewTest, isOnlyClass = false }) => {
  const [isExpanded, setIsExpanded] = useState(isOnlyClass);
  const passedTests = cohort.tests.filter(t => t.passed).length;

  return (
    <div className="mb-8">
      <div
        className="flex items-center justify-between p-4 bg-gray-50 rounded-t-lg cursor-pointer gap-3"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="min-w-0">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-800 break-words">{cohort.name}</h2>
          {cohort.description && (
            <p className="text-sm text-gray-600 mt-1">{cohort.description}</p>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
          <div className="text-xs sm:text-sm text-gray-600 text-right">
            {passedTests}/{cohort.tests.length} Tests Completed
          </div>
          {isExpanded ? (
            <ChevronUp className="h-5 w-5 text-gray-500" />
          ) : (
            <ChevronDown className="h-5 w-5 text-gray-500" />
          )}
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-l border-r border-b rounded-b-lg">
          {cohort.tests.map(test => (
            <TestCard
              key={test.id}
              test={test}
              onStartTest={onStartTest}
              onReviewTest={onReviewTest}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AvailableTests = ({ user, onStartTest, onReviewTest }) => {
  const [cohorts, setCohorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEnrollmentModal, setShowEnrollmentModal] = useState(false);
  const [hasEnrollment, setHasEnrollment] = useState(false);
  const navigate = useNavigate();

useEffect(() => {
  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/tests/available/${user.id}`,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) throw new Error('Failed to fetch available tests');
      
      const { cohorts: data, hasEnrollment: enrolled } = await response.json();

      logger.info('tests_fetched', {
        userId: user.id,
        cohortCount: data.length,
        totalTests: data.reduce((sum, cohort) => sum + cohort.tests.length, 0),
        hasEnrollment: enrolled
      });

      setCohorts(data);
      setHasEnrollment(enrolled);

      // Auto-show enrollment modal only if student has no enrollment at all
      if (data.length === 0 && !enrolled) {
        setShowEnrollmentModal(true);
      }
    } catch (error) {
      logger.error('tests_fetch_failed', {
        userId: user.id,
        error: error.message
      });
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  fetchTests();
}, [user.id]);

  if (loading) return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="flex justify-center items-center h-32">
        <p className="text-gray-500">Loading tests...</p>
      </CardContent>
    </Card>
  );

  if (error) return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardContent className="flex justify-center items-center h-32">
        <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
        <p className="text-red-500">Error: {error}</p>
      </CardContent>
    </Card>
  );

  const totalTests = cohorts.reduce((sum, cohort) => sum + cohort.tests.length, 0);
  const totalPassed = cohorts.reduce((sum, cohort) => sum + cohort.tests.filter(t => t.passed).length, 0);

  return (
    <>
      <EnrollmentModal
        open={showEnrollmentModal}
        onOpenChange={setShowEnrollmentModal}
        userId={user.id}
        isRequired={!hasEnrollment}
      />

      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-lg sm:text-xl">Available Tests for {user.firstName}</span>
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowEnrollmentModal(true)}
                className="flex items-center gap-2 min-h-[44px]"
              >
                <UserPlus className="h-4 w-4" />
                Join Class
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/grades')}
                className="flex items-center gap-2 min-h-[44px]"
              >
                <GraduationCap className="h-4 w-4" />
                My Grades
              </Button>
              {/* Total progress indicator */}
              {cohorts.length > 0 && (
                <div className="text-sm font-normal text-gray-600">
                  {totalPassed}/{totalTests} Tests Completed
                </div>
              )}
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6">
          {cohorts.length === 0 ? (
            <div className="text-center py-12">
              <BookOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                {hasEnrollment ? 'No Tests Available' : 'No Classes Yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {hasEnrollment
                  ? 'Your class doesn\'t have any tests assigned yet. Check back later!'
                  : 'You haven\'t been assigned to any classes yet.'}
              </p>
              {!hasEnrollment && (
                <Button onClick={() => setShowEnrollmentModal(true)} size="lg">
                  <UserPlus className="h-5 w-5 mr-2" />
                  Request to Join a Class
                </Button>
              )}
            </div>
          ) : (
            cohorts.map(cohort => (
          <CohortSection
            key={cohort.id}
            cohort={cohort}
            onStartTest={onStartTest}
            onReviewTest={onReviewTest}
            isOnlyClass={cohorts.length === 1}
          />
        )))}
        </CardContent>
      </Card>
    </>
  );
};

export default AvailableTests;